<template>
  <div v-if="isAnalyzing" class="row">
    <div class="col">
      <span>analyzing...</span>
    </div>
  </div>
  <div v-else class="row">
    <div class="col">
      <div class="row">
        <div class="col">
          <div ref="canvasContainer" class="custom-canvas-container">
            <canvas ref="canvasImage" @click="handleCanvasClick"></canvas>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="input-group mt-2">
            <button class="btn btn-outline-secondary" @click="prevImage" :disabled="currentIndex === 0">
              <i class="fa-solid fa-caret-left"></i>
            </button>
            <span class="input-group-text">Image {{ currentIndex + 1 }} of {{ imageData.images.length }}</span>
            <button class="btn btn-outline-secondary" @click="nextImage" :disabled="currentIndex === imageData.images.length - 1">
              <i class="fa-solid fa-caret-right"></i>
            </button>
          </div>
        </div>
        <div class="col">
          <div class="custom-commands-right">
            <div class="input-group mt-2">
              <button class="btn btn-outline-secondary" title="reset" @click="analyze">Analyze</button>
            </div>
          </div>
        </div>
        <div class="col">
          <div class="custom-commands-right">
            <div class="input-group mt-2">
              <button class="btn btn-outline-secondary" title="zoom in" @click="zoomIn"><i class="fa-solid fa-magnifying-glass-plus"></i></button>
              <button class="btn btn-outline-secondary" title="reset" @click="resetZoom"><i class="fa-solid fa-expand"></i></button>
              <button class="btn btn-outline-secondary" title="zoom out" @click="zoomOut"><i class="fa-solid fa-magnifying-glass-minus"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'FilePreview',
  props: {
    imageData: String,
    isAnalyzing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageSrc: '',
      canvasRef: null,
      ctx: null,
      polygons: [],
      zoomFactor: 1,
      baseScaleFactor: 1,
      currentIndex: 0,
      selectedPolygon: null,
      hoveredPolygon: null,
      dragStart: { x: 0, y: 0 }, // Track the starting mouse position for dragging
      imageOffset: { x: 0, y: 0 }, // Track the current image position
      isDragging: false // Flag to track if dragging is in progress
    }
  },
  mounted() {
    this.canvasRef = this.$refs.canvasImage
    this.ctx = this.canvasRef.getContext('2d')
    this.canvasRef.addEventListener('mousemove', this.handleMouseMove)
    this.canvasRef.addEventListener('click', this.handleCanvasClick)
    this.canvasRef.addEventListener('mousedown', this.handleMouseDown)
    this.canvasRef.addEventListener('mouseup', this.handleMouseUp)
    this.canvasRef.addEventListener('mouseout', this.handleMouseUp)
    this.loadImage()
  },
  emits: ['polygons-drawn'],
  methods: {
    loadImage() {
      const imageDataItem = this.imageData.images[this.currentIndex]
      this.imageSrc = imageDataItem.imageUrl

      const image = new Image()
      image.src = this.imageSrc
      image.onload = () => {
        const canvas = this.canvasRef
        const container = this.$refs.canvasContainer
        this.ctx = canvas.getContext('2d')

        // Calculate the scaling factor to fit the image width-wise
        this.baseZoom = container.clientWidth / image.width // Zoom factor to fit width
        this.zoomFactor = this.baseZoom

        // Calculate the scaled height to maintain aspect ratio
        const scaledHeight = image.height * this.zoomFactor

        // Set canvas width (fit to container width)
        canvas.width = container.clientWidth

        // Set canvas height based on aspect ratio
        canvas.height = scaledHeight

        // Apply zoom and centering
        this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, 0, 0)
        this.ctx.drawImage(image, 0, 0, image.width, image.height)

        this.polygons = imageDataItem.words || []
        this.drawPolygons()
      }
    },
    drawPolygons() {
      if (!this.ctx) return

      const image = new Image()
      image.src = this.imageSrc
      image.onload = () => {
        // Clear canvas before drawing
        this.ctx.setTransform(1, 0, 0, 1, 0, 0)
        this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height)

        // Apply zoom and offset transformations
        this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, this.imageOffset.x, this.imageOffset.y)

        // Draw the image at the origin (0, 0) since the offset is already applied in setTransform
        this.ctx.drawImage(image, 0, 0, image.width, image.height)

        // Draw polygons
        this.polygons.forEach(({ polygon, content }) => {
          const isHovered = this.hoveredPolygon && this.hoveredPolygon.content === content
          const isSelected = this.selectedPolygon && this.selectedPolygon.content === content

          this.ctx.beginPath()
          for (let i = 0; i < polygon.length; i += 2) {
            let x = polygon[i]
            let y = polygon[i + 1]
            if (i === 0) {
              this.ctx.moveTo(x, y)
            } else {
              this.ctx.lineTo(x, y)
            }
          }
          this.ctx.closePath()

          // Set styles for hover/selection
          this.ctx.strokeStyle = isSelected ? 'blue' : isHovered ? 'yellow' : 'red'
          this.ctx.fillStyle = isSelected ? 'rgba(0, 0, 255, 0.2)' : isHovered ? 'rgba(255, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.2)'
          this.ctx.fill()
          this.ctx.stroke()
        })
        this.$emit('polygons-drawn', this.selectedPolygon)
      }
    },
    applyZoom() {
      const image = new Image()
      image.src = this.imageSrc

      image.onload = () => {
        const canvas = this.canvasRef
        const container = this.$refs.canvasContainer
        this.ctx = canvas.getContext('2d')

        // Calculate the scaled height based on zoom factor
        const scaledHeight = image.height * this.zoomFactor

        // Set canvas width (same as before)
        canvas.width = container.clientWidth

        // Set the new height based on zoom factor
        canvas.height = scaledHeight

        // Apply zoom and offset transformations
        this.ctx.setTransform(this.zoomFactor, 0, 0, this.zoomFactor, this.imageOffset.x, this.imageOffset.y)
        this.ctx.clearRect(0, 0, canvas.width, canvas.height) // Clear canvas before redrawing

        // Draw the image at the origin (0, 0) since the offset is already applied in setTransform
        this.ctx.drawImage(image, 0, 0, image.width, image.height)

        // Redraw polygons
        this.drawPolygons()
      }
    },
    zoomIn() {
      this.zoomFactor *= 1.2
      this.applyZoom()
    },
    zoomOut() {
      this.zoomFactor /= 1.2
      this.applyZoom()
    },
    resetZoom() {
      this.zoomFactor = this.baseZoom
      this.applyZoom()
    },
    prevImage() {
      if (this.currentIndex > 0) {
        this.currentIndex--
        this.selectedPolygon = null
        this.hoveredPolygon = null
        this.loadImage()
      }
    },
    nextImage() {
      if (this.currentIndex < this.imageData.images.length - 1) {
        this.currentIndex++
        this.selectedPolygon = null
        this.hoveredPolygon = null
        this.loadImage()
      }
    },
    handleCanvasClick(event) {
      this.isDragging = false
      const { x, y } = this.getMousePosition(event)
      this.selectedPolygon = null
      this.hoveredPolygon = null
      for (const polygonData of this.polygons) {
        if (this.isPointInPolygon(x, y, polygonData.polygon)) {
          this.selectedPolygon = polygonData
          this.hoveredPolygon = null
          this.drawPolygons()
          return
        }
      }
    },
    handleMouseDown(event) {
      this.isDragging = true
      this.dragStart.x = event.clientX - this.imageOffset.x
      this.dragStart.y = event.clientY - this.imageOffset.y
    },
    handleMouseUp() {
      this.isDragging = false
      this.drawPolygons()
    },
    handleMouseMove(event) {
      const { x, y } = this.getMousePosition(event)
      if (this.isDragging) {
        this.imageOffset.x = event.clientX - this.dragStart.x
        this.imageOffset.y = event.clientY - this.dragStart.y
        requestAnimationFrame(this.redrawImage)
      } else {
        // Handle hover logic
        let isHovering = false
        let hovered = null

        this.polygons.forEach((polygon) => {
          if (this.isPointInPolygon(x, y, polygon.polygon)) {
            isHovering = true
            hovered = polygon
          }
        })

        if (isHovering) {
          this.hoveredPolygon = hovered
        } else {
          this.hoveredPolygon = null
        }

        this.drawPolygons()
      }
    },
    redrawImage() {
      const image = new Image()
      image.src = this.imageSrc
      image.onload = () => {
        this.ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height)
        this.ctx.drawImage(image, this.imageOffset.x, this.imageOffset.y, image.width, image.height)
      }
    },
    getMousePosition(event) {
      const rect = this.canvasRef.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      const mouseY = event.clientY - rect.top

      // Get the transformation matrix of the canvas
      const transform = this.ctx.getTransform()

      // Invert the transformation matrix to get the correct coordinates
      const invertedTransform = transform.invertSelf()
      const x = invertedTransform.a * mouseX + invertedTransform.c * mouseY + invertedTransform.e
      const y = invertedTransform.b * mouseX + invertedTransform.d * mouseY + invertedTransform.f

      return { x, y }
    },
    isPointInPolygon(x, y, polygon) {
      let inside = false
      const numPoints = polygon.length / 2

      for (let i = 0, j = numPoints - 1; i < numPoints; j = i++) {
        const xi = polygon[i * 2],
          yi = polygon[i * 2 + 1]
        const xj = polygon[j * 2],
          yj = polygon[j * 2 + 1]

        const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
        if (intersect) inside = !inside
      }
      return inside
    },
    analyze() {}
  },
  beforeUnmount() {
    this.canvasRef.removeEventListener('mousemove', this.handleMouseMove)
    this.canvasRef.removeEventListener('click', this.handleCanvasClick)
    this.canvasRef.removeEventListener('mousedown', this.handleMouseDown)
    this.canvasRef.removeEventListener('mouseup', this.handleMouseUp)
    this.canvasRef.removeEventListener('mouseout', this.handleMouseUp)
  }
}
</script>
<style lang="scss" scoped>
.custom-image-viewer {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.custom-canvas-container {
  border: 1px solid #ddd;
  width: 50vw;
  height: 60vh;
  overflow: hidden;
  padding: 10px;
}
.custom-commands-right {
  float: right;
  width: 140px;
}
</style>
